import { RoundedBox } from '@react-three/drei';
import {
    memo,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import type {
    Material,
    Mesh,
    Scene,
} from 'three';
import type { ModelViewerPedestalLayout } from '../Pedestal';
import type { StudioPedestalBodyLayout } from './PedestalGeometry';
import { createBeveledCylinderProfile } from './PedestalGeometry';
import { STUDIO_REFLECTION_CAPTURE_ACTIVE_USER_DATA_KEY } from './config';

interface Props {
    body: StudioPedestalBodyLayout
    layout: ModelViewerPedestalLayout
}

/**
 * The planar reflector uses a mirrored camera, so it cannot reuse the main
 * camera's screen-space transmission buffer. This cheap clearcoat proxy is
 * colorless during normal/transmission passes and appears only while the floor
 * reflection is being captured.
 */
export default memo(function PedestalReflectionProxy({
    body,
    layout,
}:Props) {
    const proxyRef = useRef<Mesh>(null);
    const cylinderProfile = useMemo(() => createBeveledCylinderProfile(
        layout.radius,
        body.height,
        body.cylinderBevel,
    ), [
        body.cylinderBevel,
        body.height,
        layout.radius,
    ]);
    const updateVisibility = useCallback((_renderer:unknown, scene:Scene) => {
        const proxyMaterial = proxyRef.current?.material as Material | undefined;
        if(!proxyMaterial) return;

        proxyMaterial.colorWrite = (
            scene.userData[
                STUDIO_REFLECTION_CAPTURE_ACTIVE_USER_DATA_KEY
            ] === true
        );
    }, []);
    const hideProxy = useCallback(() => {
        const proxyMaterial = proxyRef.current?.material as Material | undefined;
        if(proxyMaterial) proxyMaterial.colorWrite = false;
    }, []);
    const proxyMaterial = <meshPhysicalMaterial
        clearcoat={1}
        clearcoatRoughness={0.08}
        color="#eaf3ff"
        colorWrite={false}
        depthWrite={false}
        envMapIntensity={1.2}
        ior={1.49}
        metalness={0}
        opacity={0.16}
        roughness={0.12}
        transparent
    />;

    return layout.shape === 'cylinder'
        ? <mesh
            ref={proxyRef}
            frustumCulled={false}
            onAfterRender={hideProxy}
            onBeforeRender={updateVisibility}
            position={[0, body.centerY, 0]}
        >
            <latheGeometry args={[cylinderProfile, 192]} />
            {proxyMaterial}
        </mesh>
        : <RoundedBox
            ref={proxyRef}
            args={[layout.width, body.height, layout.depth]}
            bevelSegments={6}
            frustumCulled={false}
            onAfterRender={hideProxy}
            onBeforeRender={updateVisibility}
            position={[0, body.centerY, 0]}
            radius={body.boxEdgeRadius}
            smoothness={8}
        >
            {proxyMaterial}
        </RoundedBox>;
});
